/**
 * SalesExecDashboard — Sales Executive / Telecaller desk.
 *
 * Ported from `SalesExecDashboard` in aura-exec.jsx.
 * Live call bar + today's call queue + my deals + proposals awaiting reply +
 * incentive tier + today's scorecard + last-call AI summary + weekly digest.
 *
 * UI-only. Tenant-safe (module-gated, no slug hardcoding).
 */

import { useEffect, useState } from 'react';
import { Phone, Sparkles, Users, Mic } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import {
  safeGet,
  formatINRCompact,
  formatNum,
  todayLabel,
  formatTimeShort,
  makeCreditBurn,
} from './_execShared';

// ─── Types ────────────────────────────────────────────────────────────────

interface CallQueueItem {
  id: string;
  contactName?: string;
  contact?: { firstName?: string; lastName?: string; name?: string };
  accountName?: string;
  account?: { name?: string };
  phone?: string;
  phoneNumber?: string;
  productInterest?: string;
  product?: string;
  scheduledTime?: string;
  time?: string;
  priority?: 'high' | 'med' | 'medium' | 'low';
  status?: string;
  note?: string;
  lastOutcome?: string;
}

interface IncentivePayload {
  earned?: number;
  target?: number;
  tier?: string;
  nextTier?: string;
}

interface DealRow {
  id: string;
  name?: string;
  stageName?: string;
  stage?: { name?: string };
  value?: number;
  probability?: number;
  stalled?: boolean;
}

interface ProposalRow {
  id: string;
  number?: string;
  title?: string;
  account?: { name?: string };
  status?: string;
  sentAt?: string;
  value?: number;
}

interface PersonalStats {
  callsMade?: number;
  connected?: number;
  proposalsSent?: number;
  won?: number;
}

// ─── Atoms (inline to avoid new deps) ─────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--ivory-3)',
        color: 'var(--ink-1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 10,
        background: bg,
        color,
        fontWeight: 500,
        letterSpacing: 0.02,
      }}
    >
      {children}
    </span>
  );
}

// ─── Incentive bar ────────────────────────────────────────────────────────

function IncentiveBar() {
  const [data, setData] = useState<IncentivePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    safeGet<IncentivePayload>('/role-kpis/telecaller/incentive-progress').then((r) => {
      setData(r.data);
      setErr(r.error);
      setLoading(false);
    });
  }, []);

  if (loading) return <Skeleton h={110} />;
  if (err || !data || !data.target) {
    return (
      <div
        className="card"
        style={{
          padding: 14,
          background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
          border: '1px solid var(--saffron-100)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--saffron-600)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            fontWeight: 600,
          }}
        >
          Incentive tier
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          No incentive plan configured
        </div>
      </div>
    );
  }

  const earned = data.earned ?? 0;
  const target = data.target ?? 1;
  const pct = Math.min(100, (earned / target) * 100);
  const diff = Math.max(0, target - earned);

  return (
    <div
      className="card"
      style={{
        padding: 16,
        background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
        border: '1px solid var(--saffron-100)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 10 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--saffron-600)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              fontWeight: 600,
            }}
          >
            {data.tier ?? 'Bronze'} tier · this month
          </div>
          <div
            className="num-display"
            style={{ fontSize: 24, fontWeight: 500, letterSpacing: -0.02, marginTop: 4 }}
          >
            {formatINRCompact(earned)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            of {formatINRCompact(target)}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: 'var(--ink-2)', textAlign: 'right' }}>
          Next: <strong style={{ color: 'var(--saffron-600)' }}>{data.nextTier ?? '—'}</strong>
          <div style={{ fontSize: 10, marginTop: 2 }}>{formatINRCompact(diff)} more</div>
        </div>
      </div>
      <div
        style={{
          height: 6,
          background: 'color-mix(in srgb, var(--ivory-0) 50%, transparent)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--saffron)' }} />
      </div>
    </div>
  );
}

// ─── Live dialer strip ────────────────────────────────────────────────────

function DialerStrip({ onCallNext, lineBusy, lang }: { onCallNext: () => void; lineBusy: boolean; lang: 'EN' | 'HI' }) {
  return (
    <div
      className="card"
      style={{
        padding: 14,
        marginBottom: 14,
        background: lineBusy ? '#1B1713' : 'var(--ivory-1)',
        color: lineBusy ? 'var(--ivory-0)' : 'var(--ink-0)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 99,
          background: lineBusy ? 'var(--saffron)' : 'var(--ivory-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: lineBusy ? 'pulse 1.6s infinite' : 'none',
        }}
      >
        <Phone size={18} color={lineBusy ? '#1B1713' : 'currentColor'} />
      </div>
      <div style={{ flex: 1 }}>
        {lineBusy ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
              🔴 On call · live
            </div>
            <div
              className="num"
              style={{ fontSize: 11, opacity: 0.7 }}
            >
              Exotel · Sarvam transcribing…
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
              {lang === 'HI' ? 'अगला कॉल तैयार है' : 'Ready for next call'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {lang === 'HI'
                ? 'एक क्लिक में डायल करें · Sarvam + Exotel'
                : '1-click dial · Sarvam voice + Exotel'}
            </div>
          </>
        )}
      </div>
      {!lineBusy && (
        // Apr 27 (audit P1) — Voicemail navigates to call history filtered by missed/voicemail.
        <a
          href="/calls?filter=voicemail"
          className="btn btn-outline btn-sm"
          style={{ textDecoration: 'none' }}
        >
          Voicemail
        </a>
      )}
      <button className="btn btn-primary" onClick={onCallNext} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Phone size={12} /> {lang === 'HI' ? 'अगला कॉल' : 'Call next'}
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function SalesExecDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user } = useAuthStore();
  const firstName = user?.firstName ?? 'there';

  const [lang, setLang] = useState<'EN' | 'HI'>('EN');
  const [lineBusy, setLineBusy] = useState(false);

  const [queue, setQueue] = useState<CallQueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueErr, setQueueErr] = useState<string | null>(null);

  const [deals, setDeals] = useState<DealRow[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);

  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(true);

  const [stats, setStats] = useState<PersonalStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const burn = makeCreditBurn();

  useEffect(() => {
    safeGet<{ items?: CallQueueItem[]; queue?: CallQueueItem[] } | CallQueueItem[]>(
      '/telecaller/today',
    ).then((r) => {
      const raw = r.data;
      const parsed: CallQueueItem[] = Array.isArray(raw)
        ? raw
        : (raw as { items?: CallQueueItem[]; queue?: CallQueueItem[] } | null)?.items ??
          (raw as { queue?: CallQueueItem[] } | null)?.queue ??
          [];
      setQueue(parsed);
      setQueueErr(r.error);
      setQueueLoading(false);
    });

    safeGet<{ items?: DealRow[] } | DealRow[]>('/deals?assignedToMe=1&status=open&limit=5').then(
      (r) => {
        const raw = r.data;
        const parsed = Array.isArray(raw) ? raw : (raw as { items?: DealRow[] } | null)?.items ?? [];
        setDeals(parsed);
        setDealsLoading(false);
      },
    );

    safeGet<{ items?: ProposalRow[] } | ProposalRow[]>(
      '/proposals?assignedToMe=1&status=sent&limit=5',
    ).then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: ProposalRow[] } | null)?.items ?? [];
      setProposals(parsed);
      setProposalsLoading(false);
    });

    safeGet<PersonalStats>('/role-kpis/telecaller/personal').then((r) => {
      setStats(r.data);
      setStatsLoading(false);
    });
  }, []);

  const priCol: Record<string, string> = {
    high: 'var(--danger, #B8563B)',
    med: 'var(--saffron-600)',
    medium: 'var(--saffron-600)',
    low: 'var(--ink-3)',
  };

  const getContactName = (q: CallQueueItem) => {
    if (q.contactName) return q.contactName;
    if (q.contact?.name) return q.contact.name;
    if (q.contact?.firstName || q.contact?.lastName)
      return [q.contact.firstName, q.contact.lastName].filter(Boolean).join(' ');
    return 'Unnamed';
  };

  const callNext = () => {
    setLineBusy((b) => !b);
  };

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Sales Executive · Inside sales desk
          </div>
          <h2
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 24,
              fontWeight: 500,
              letterSpacing: -0.02,
              marginBottom: 2,
            }}
          >
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{todayLabel()}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: 'flex',
            background: 'var(--ivory-2)',
            padding: 2,
            borderRadius: 6,
            fontSize: 11,
          }}
        >
          {(['EN', 'HI'] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              style={{
                border: 'none',
                background: lang === l ? 'var(--ink-0)' : 'transparent',
                color: lang === l ? 'var(--ivory-0)' : 'var(--ink-2)',
                padding: '4px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontWeight: 500,
              }}
            >
              {l}
            </button>
          ))}
        </div>
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See team view
          </button>
        )}
      </div>

      {/* Live dialer */}
      <DialerStrip onCallNext={callNext} lineBusy={lineBusy} lang={lang} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14 }}>
        <div>
          {/* Call queue */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>Today's call queue</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{queue.length} queued</Chip>
            </span>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ai btn-sm"
              onClick={() => burn(8, 'telecaller_reprioritize')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Sparkles size={11} /> Re-prioritize · 8 cr
            </button>
          </div>

          {queueLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} h={56} />
              ))}
            </div>
          ) : queueErr ? (
            <div className="card" style={{ padding: 16, marginBottom: 18 }}>
              <NoData label="Call queue unavailable" />
            </div>
          ) : queue.length === 0 ? (
            <div className="card" style={{ padding: 16, marginBottom: 18 }}>
              <NoData label="No calls queued today. Add leads or wait for auto-assign." />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {queue.slice(0, 12).map((q) => {
                const pri = (q.priority ?? 'med').toLowerCase();
                const accent = priCol[pri] ?? 'transparent';
                const name = getContactName(q);
                const acc = q.accountName ?? q.account?.name ?? '';
                const phone = q.phone ?? q.phoneNumber ?? '';
                const t = q.scheduledTime ?? q.time ?? '';
                const product = q.productInterest ?? q.product ?? '';
                return (
                  <div
                    key={q.id}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 10,
                      borderLeft: `3px solid ${accent}`,
                    }}
                  >
                    <div
                      className="num"
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        width: 42,
                        textAlign: 'center',
                      }}
                    >
                      {t ? formatTimeShort(t) : '—'}
                    </div>
                    <Avatar name={name} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          marginBottom: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {name}
                        {acc && (
                          <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}> · {acc}</span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          display: 'flex',
                          gap: 6,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        {phone && <span className="num">{phone}</span>}
                        {product && (
                          <>
                            <span>·</span>
                            <span>{product}</span>
                          </>
                        )}
                        <span>·</span>
                        <span
                          style={{
                            color: accent,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: 10,
                          }}
                        >
                          ● {pri}
                        </span>
                      </div>
                      {(q.note ?? q.lastOutcome) && (
                        <div style={{ marginTop: 4 }}>
                          <Chip color="var(--ink-2)">💡 {q.note ?? q.lastOutcome}</Chip>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn btn-ai btn-sm"
                        onClick={() => burn(3, 'ai_opening_line')}
                        title="AI-draft opening line"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Sparkles size={11} /> 3
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        onClick={() => {
                          setLineBusy(true);
                          setQueue((prev) =>
                            prev.map((x) => (x.id === q.id ? { ...x, status: 'dialing' } : x)),
                          );
                        }}
                      >
                        <Phone size={12} /> Call
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* My active deals */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>My active deals</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{deals.length}</Chip>
            </span>
          </div>
          {dealsLoading ? (
            <Skeleton h={120} />
          ) : deals.length === 0 ? (
            <div className="card" style={{ padding: 16, marginBottom: 14 }}>
              <NoData label="No deals assigned to you yet" />
            </div>
          ) : (
            <div className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--ivory-1)', color: 'var(--ink-3)' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>Deal</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>
                      Stage
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      Value
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      Prob.
                    </th>
                    <th style={{ padding: '8px 10px' }} />
                  </tr>
                </thead>
                <tbody>
                  {deals.slice(0, 5).map((d) => {
                    const stage = d.stageName ?? d.stage?.name ?? '—';
                    const prob = d.probability ?? 0;
                    return (
                      <tr key={d.id} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '8px 10px' }}>
                          {d.stalled && <span style={{ color: 'var(--danger, #B8563B)' }}>⚠ </span>}
                          {d.name ?? 'Unnamed'}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <Chip>{stage}</Chip>
                        </td>
                        <td
                          className="num"
                          style={{ padding: '8px 10px', textAlign: 'right' }}
                        >
                          {formatINRCompact(d.value ?? 0)}
                        </td>
                        <td
                          className="num"
                          style={{
                            padding: '8px 10px',
                            textAlign: 'right',
                            color: prob >= 60 ? 'var(--forest-600)' : 'var(--ink-2)',
                          }}
                        >
                          {prob}%
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                          <button
                            className="btn btn-ai btn-sm"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={() => burn(5, 'ai_deal_draft')}
                          >
                            <Sparkles size={10} /> Draft · 5
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Proposals awaiting reply */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>Proposals awaiting reply</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{proposals.length}</Chip>
            </span>
          </div>
          {proposalsLoading ? (
            <Skeleton h={80} />
          ) : proposals.length === 0 ? (
            <div className="card" style={{ padding: 16 }}>
              <NoData label="No open proposals awaiting customer reply" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {proposals.slice(0, 5).map((p) => (
                <div
                  key={p.id}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 10,
                  }}
                >
                  <div
                    className="num"
                    style={{ fontSize: 11, color: 'var(--ink-3)', width: 90 }}
                  >
                    {p.number ?? p.id}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{p.title ?? 'Proposal'}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                      {p.account?.name ?? ''}
                    </div>
                  </div>
                  <div className="num" style={{ fontSize: 12 }}>
                    {formatINRCompact(p.value ?? 0)}
                  </div>
                  {/* Apr 27 (audit P1) — Nudge opens AI panel with proposal-followup preset. */}
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'nudge_proposal', proposalId: p.id } }))}
                  >
                    Nudge
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <IncentiveBar />

          <div className="card" style={{ padding: 14 }}>
            <div
              className="kpi-label"
              style={{ marginBottom: 10 }}
            >
              Today's scorecard
            </div>
            {statsLoading ? (
              <Skeleton h={100} />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  fontSize: 12,
                }}
              >
                <div>
                  <div className="num-display" style={{ fontSize: 20 }}>
                    {formatNum(stats?.callsMade ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Calls made</div>
                </div>
                <div>
                  <div
                    className="num-display"
                    style={{ fontSize: 20, color: 'var(--forest-600)' }}
                  >
                    {formatNum(stats?.connected ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Connected</div>
                </div>
                <div>
                  <div className="num-display" style={{ fontSize: 20 }}>
                    {formatNum(stats?.proposalsSent ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Proposals sent</div>
                </div>
                <div>
                  <div
                    className="num-display"
                    style={{ fontSize: 20, color: 'var(--forest-600)' }}
                  >
                    {formatNum(stats?.won ?? 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Won</div>
                </div>
              </div>
            )}
          </div>

          {/* Last call AI summary (placeholder when no endpoint) */}
          <div className="card" style={{ padding: 14 }}>
            <div className="kpi-label" style={{ marginBottom: 8 }}>
              Last call summary
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mic size={12} />
              <span>Complete a call to see the Sarvam-powered AI summary here.</span>
            </div>
            <button
              className="btn btn-ai btn-sm"
              style={{ width: '100%', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
              onClick={() => burn(5, 'ai_weekly_digest')}
            >
              <Sparkles size={11} /> Refresh digest · 5 cr
            </button>
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default SalesExecDashboard;
